import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_12: GgxTourDefinition = {
    key: '[HOWTO-12] Pre-Production Human Integrated Testing',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 7,
    prerequisite: {
        target: '[test-id="home-page-card-Human Integrated Testing"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: "Step into your users' shoes",
        subtitle:
            'It is tempting to think that once a pipeline is built, it is good to go. Before ' +
            'it reaches real customers, though, someone should use it the way they will: ask ' +
            'the questions they ask, and judge the answers they get. That is what Human ' +
            'Integrated Testing is for. In this tour you will open a feedback portal, run a ' +
            'test conversation as a customer, rate the reply, hand your notes to the team, ' +
            'and see how all that feedback adds up across sessions.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You ran a test session as Henry Adams, rated the reply, wrote up your notes and ' +
            'saved the session, then saw how every session rolls up into the Insights page: ' +
            'which tests passed, which failed, which are still waiting for feedback, and how ' +
            'each intent is doing over time. Every session you save also becomes ground truth ' +
            'the team can rerun automatically as the pipeline changes. Next, Running your ' +
            'first evaluation shows how to measure the same pipeline with a report instead of ' +
            'by hand.',
        learned: [
            'A **test session** pairs an expected intent with a persona.',
            '**Rating and notes** hand feedback straight to the builders.',
            'The **Insights page** rolls every session into pass, fail and pending.',
        ],
    },
    chapters: [
        {
            name: 'Find the portal',
            blurb: 'From the home page to the feedback portal',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'Set up a test',
            blurb: 'New test, expected intent and persona',
            icon: 'compass',
            steps: [5, 6, 7, 8],
        },
        {
            name: 'Chat and rate',
            blurb: 'Ask as a customer, judge the reply',
            icon: 'pulse',
            steps: [9, 10],
        },
        {
            name: 'Close the loop',
            blurb: 'End the session and hand over your notes',
            icon: 'list',
            steps: [11, 12, 13, 14, 15],
        },
        {
            name: 'See the big picture',
            blurb: 'Insights across every session',
            icon: 'grid',
            steps: [16, 17, 18, 19],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-card-Human Integrated Testing"] [test-id="launch-button"]',
            title: 'Where people test the pipeline',
            whatHappens:
                'the module opens on Evaluations, with Feedback Portals beside it in the sidebar.',
            icon: 'map',
            description:
                '<strong>Human Integrated Testing</strong> is the module for testing a ' +
                'pipeline the way a customer would experience it: one conversation at a time, ' +
                'judged by a person. It sits beside the automated evaluations, because the two ' +
                'catch different things. Click <strong>Launch</strong> on this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Feedback Portals"]',
            scrollIntoView: true,
            title: 'One place for all human feedback',
            whatHappens: 'the Feedback Portals page opens with the portals in this project.',
            icon: 'map',
            description:
                'A <strong>Feedback Portal</strong> is a shared testing space for one ' +
                'pipeline. Anyone on the team can open it, run a conversation as a customer, ' +
                'rate each reply and leave notes, and all of it lands in one place instead of ' +
                'in scattered messages and spreadsheets. That is what turns a pile of ' +
                'observations into something the team can act on. Click <strong>Feedback ' +
                'Portals</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Feedback Portals',
        },
        {
            target: '[test-id^="grid-row-link-Combined Customer Intent and Response"]',
            targetText: 'Combined Customer Intent and Response',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="FeedbackPortal-row-"]',
            title: 'Open the portal for our chatbot',
            whatHappens: 'the portal opens, listing every test session run so far.',
            icon: 'search',
            description:
                'This portal belongs to the <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>, the credit card chatbot from the other tours. The ' +
                '<strong># Tests</strong> column shows how many conversations the team has ' +
                'already run through it. Click the row to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response',
        },
        {
            target: '[test-id="start-chat"]',
            title: 'Start your own test session',
            whatHappens: 'a new session opens in a panel on the right.',
            icon: 'compass',
            description:
                'Each row on this page is one test session: a conversation someone had with ' +
                'the pipeline, with its rating and status. The team has already run a few. Now ' +
                'you will run your own. Click <strong>New Test</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'New Test',
        },
        {
            target: '[test-id^="dynamic-field-select-"]',
            title: 'Say what you expect before you ask',
            whatHappens: 'the list of intents this pipeline understands opens.',
            icon: 'list',
            description:
                'This chatbot works out what the customer wants, its <strong>intent</strong>, ' +
                'before it answers. Writing down the intent you expect, before you type a ' +
                'word, turns your chat into a real test: afterwards GGX can tell whether the ' +
                'pipeline understood the request or guessed wrong. Click the <strong>Expected ' +
                'Intent</strong> box to see the intents.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Expected Intent',
        },
        {
            target: '[test-id^="dynamic-field-option-"]',
            targetText: 'BLOCK CARD',
            scrollIntoView: true,
            title: 'We will test a customer blocking a card',
            whatHappens: 'the list closes with BLOCK CARD selected.',
            icon: 'list',
            description:
                'These are all the things this chatbot is built to handle, from activating a ' +
                'card to cancelling a mortgage. A customer who has lost a card is a common and ' +
                'high-stakes case, so that is the one we will try. Click <strong>BLOCK ' +
                'CARD</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceWhenAbsent: '.p-select-overlay',
            waitCue: 'Pick BLOCK CARD to continue',
        },
        {
            target: '[test-id="persona-Henry Adams"]',
            title: 'Who are you pretending to be?',
            whatHappens: "the bot answers using the selected persona's mock account data.",
            icon: 'grid',
            description:
                'Real customers are not all alike: one is annoyed because a card was just ' +
                'declined, another is excited to redeem points. Personas are sample customers ' +
                'with mock accounts and card activity, so you can test those situations without ' +
                'touching real data. <strong>Henry Adams</strong> has active credit cards, ' +
                'which is exactly what we need. Hover over his name to see his details, then ' +
                'click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="message-input"]',
            title: 'Ask it the way Henry would',
            whatHappens:
                'the bot replies above your message, and the tour moves on by itself once the ' +
                'answer arrives.',
            icon: 'pulse',
            description:
                'Type as if you were Henry. Click the tag to copy his request, paste it into ' +
                'the message box and press <strong>Enter</strong> or the arrow button.<br><br>' +
                '<code data-ggx-copy>I want to block my VISA card ending in 9010</code>',
            side: 'top',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Send your message to continue',
        },
        {
            target: '[test-id="transcript-rating"]',
            title: 'Was that a good answer?',
            whatHappens: 'your rating is saved with this reply for the team to review.',
            icon: 'grid',
            description:
                'The bot should have asked Henry to confirm before blocking the card. If it ' +
                'did, give the reply a <strong>Thumbs Up</strong>. If something is off, a ' +
                '<strong>Thumbs Down</strong> lets you pick a reason and add a comment, which ' +
                'is what turns "this felt wrong" into something a developer can fix. Rate the ' +
                'reply, then click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="end-session-button"]',
            title: 'Wrap up the session',
            whatHappens: 'a Review Session dialog opens for your overall notes.',
            icon: 'list',
            description:
                'Every message and rating is already saved as you go. Ending the session is ' +
                'where you add the bigger picture: how the conversation went as a whole and ' +
                'what the team should look at. Click <strong>End Session</strong>.',
            side: 'top',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'End Session',
        },
        {
            target: '[test-id="feedback-summary"]',
            frame: true,
            title: 'Your notes, in your words',
            whatHappens: 'next, a couple of quick questions about the intent.',
            icon: 'list',
            description:
                '<strong>Testing Notes</strong> is where you write what worked, where the ' +
                'bot did what you expected, and where it went off track. You do not need to ' +
                'fill it in for this tour, but in a real test this is often the most useful ' +
                'thing a developer reads. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-questions"]',
            frame: true,
            scrollIntoView: true,
            title: 'Did it understand Henry?',
            whatHappens: 'next, saving the session.',
            icon: 'grid',
            description:
                'GGX shows the intent the pipeline actually picked and asks whether you agree. ' +
                'That answer, next to the <strong>BLOCK CARD</strong> you expected earlier, is ' +
                'how intent accuracy gets measured across many tests. You can also note any ' +
                'question the bot should have asked but did not. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="save-session-btn"]',
            title: 'Save it for the team',
            whatHappens: 'the session is marked Completed and your feedback is stored with it.',
            icon: 'list',
            description:
                'This saves your notes and answers with the transcript and closes the session ' +
                'to further chat. The development team sees it in the portal right away. Click ' +
                '<strong>End Session</strong>.',
            side: 'top',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'End Session',
        },
        {
            target: '[test-id="chat-session-close"]',
            title: 'Why saving every session matters',
            whatHappens: 'the panel closes and you are back on the list of sessions.',
            icon: 'compass',
            description:
                'Testing a pipeline by hand takes a few minutes. Repeating it after every ' +
                'change would get tiresome fast. Because each session is saved with its ' +
                'expected intent and ratings, GGX builds up a set of known good and bad ' +
                'answers, and developers can rerun those as automated checks whenever the ' +
                'pipeline changes. Your ten minutes keep paying off. Click the ' +
                '<strong>X</strong> to close the panel.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Close',
        },
        {
            target: '[test-id="feedback-portal-insights"]',
            title: 'Now zoom out',
            whatHappens: 'the Insights page opens on the Summary view.',
            icon: 'grid',
            description:
                'One session tells you about one conversation. <strong>Insights</strong> puts ' +
                'every session in this portal together, so you can see how the pipeline is ' +
                'doing overall and where it keeps stumbling. Click <strong>Insights</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Insights',
        },
        {
            target: '[test-id="summary-insights"]',
            frame: true,
            scrollIntoView: true,
            title: 'The scoreboard',
            whatHappens: 'next, how performance changes over time.',
            icon: 'grid',
            description:
                'The <strong>Test Sessions Summary</strong> counts how many tests passed, ' +
                'failed, or are still waiting for someone to give feedback. <strong>Messages ' +
                'Summary</strong> does the same for individual replies. Your session from a ' +
                'minute ago is already in these numbers. Click Continue.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="default-insight-link-Performance"]',
            title: 'Is it getting better?',
            whatHappens: 'the Performance view opens.',
            icon: 'pulse',
            description:
                'A single number cannot tell you whether the last change helped. The ' +
                '<strong>Performance</strong> view shows results over time and by intent, so ' +
                'you can see improvements land. Click <strong>Performance</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Performance',
        },
        {
            target: '[test-id="performance-insights"]',
            frame: true,
            scrollIntoView: true,
            title: 'Where to focus next',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'The chart shows each day of testing, split into sessions that were all liked, ' +
                'sessions with a dislike, and sessions nobody has reviewed yet. Below it, the ' +
                'breakdown by intent shows which requests the bot handles well and which need ' +
                'work, so the team fixes what matters most first. Click Continue to finish.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
